import React, { useId } from 'react';
import PropTypes from 'prop-types';

import CustomRadioButton from '../custom_radio_button';
import { FormActions, FormHelp, FormSection } from '../form_layout';
import RequiredReasonTextarea from '../required_reason_textarea';
import {
  INITIATOR_OPTIONS,
  SCREENSHOT_TYPES,
  TEACHER_NOTE_LABEL,
  TEACHER_NOTE_MAX,
  TEACHER_NOTE_PLACEHOLDER
} from './care_request_rules';
import { IstTimePicker } from './ist_time_picker';
import RequestTimeWarningStep from '../request_time_warning';
import PayTimeWarningStep from '../pay_time_warning';
import { ist_input_value } from './care_request_rules';
import { is_pay_time_warning } from './care_ack_rules';

// "Note for teacher" on a teacher change: required, stored on the Care record, shown on the teacher dashboard later.
export function TeacherNoteField({ value, onChange, error }) {
  const id = useId();
  return (
    <div className="myt-field">
      <label htmlFor={id} className="myt-form-label">
        {TEACHER_NOTE_LABEL}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="required-reason-input"
        style={{ height: '60px' }}
        maxLength={TEACHER_NOTE_MAX}
        placeholder={TEACHER_NOTE_PLACEHOLDER}
      />
      {error ? <p className="myt-form-error">* {error}</p> : null}
    </div>
  );
}

TeacherNoteField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
};

TeacherNoteField.defaultProps = { value: '', error: '' };

// the "who asked, and when" fields every Care change flow shows. Props-only; the host owns the
// draft. A host that already has the initiator (cancel radios) or a mandatory note (apis reason)
// hides those here.
export function CareRequestFields(props) {
  const {
    draft,
    errors,
    onChange,
    showInitiatedBy,
    initiatedByFixed,
    showNote,
    noteLabel,
    notePlaceholder,
    showTeacherNote,
    requestedLabel,
    changeMadeAtText,
    bookedAt
  } = props;
  const timeId = useId();
  return (
    <FormSection title="Request details" className="care-request-fields">
      {showInitiatedBy ? (
        <div className="myt-field">
          <div className="myt-form-label">Request initiated by</div>
          {initiatedByFixed ? (
            <div data-testid="care-initiated-fixed">
              {INITIATOR_OPTIONS.find((o) => o.value === initiatedByFixed)?.label}
            </div>
          ) : (
            <div role="radiogroup" aria-label="Request initiated by" className="myt-form-radios">
              {INITIATOR_OPTIONS.map((o) => (
                <CustomRadioButton
                  key={o.value}
                  label={o.label}
                  selected={draft.initiated_by === o.value}
                  onRadioClick={() => onChange({ initiated_by: o.value })}
                />
              ))}
            </div>
          )}
          {errors.initiated_by ? <p className="myt-form-error">* {errors.initiated_by}</p> : null}
        </div>
      ) : null}
      <div className="myt-field">
        <label className="myt-form-label" htmlFor={timeId}>
          {requestedLabel}
        </label>
        <IstTimePicker
          id={timeId}
          value={draft.requested_at || ''}
          onChange={(requested_at) => onChange({ requested_at })}
          className="care-request-time"
          bookedAt={bookedAt}
        />
        {errors.requested_at ? <p className="myt-form-error">* {errors.requested_at}</p> : null}
        {changeMadeAtText ? <FormHelp>Change made at: {changeMadeAtText}</FormHelp> : null}
      </div>
      {showNote ? (
        <RequiredReasonTextarea
          label={noteLabel}
          value={draft.note || ''}
          onChange={(e) => onChange({ note: e.target.value })}
          placeholder={notePlaceholder}
          error={!!errors.note}
          errorText={errors.note}
          height="60px"
        />
      ) : null}
      {showTeacherNote ? (
        <TeacherNoteField
          value={draft.teacher_note || ''}
          onChange={(teacher_note) => onChange({ teacher_note })}
          error={errors.teacher_note}
        />
      ) : null}
      <div className="myt-field">
        <label className="myt-form-label">
          Screenshot (optional)
          <input
            type="file"
            aria-label="Screenshot (optional)"
            accept={SCREENSHOT_TYPES.join(',')}
            onChange={(e) =>
              onChange({ file: e.target.files && e.target.files[0] ? e.target.files[0] : null })
            }
          />
        </label>
        {errors.file ? <p className="myt-form-error">* {errors.file}</p> : null}
      </div>
    </FormSection>
  );
}

CareRequestFields.propTypes = {
  draft: PropTypes.shape({
    initiated_by: PropTypes.string,
    requested_at: PropTypes.string,
    note: PropTypes.string,
    teacher_note: PropTypes.string
  }).isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showInitiatedBy: PropTypes.bool,
  initiatedByFixed: PropTypes.string,
  showNote: PropTypes.bool,
  noteLabel: PropTypes.string,
  notePlaceholder: PropTypes.string,
  showTeacherNote: PropTypes.bool,
  requestedLabel: PropTypes.string,
  changeMadeAtText: PropTypes.string,
  bookedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

CareRequestFields.defaultProps = {
  errors: {},
  showInitiatedBy: true,
  initiatedByFixed: '',
  showNote: false,
  noteLabel: 'Note: how and what they asked (required)',
  notePlaceholder: 'e.g. Student asked on chat to move to Friday evening',
  showTeacherNote: false,
  requestedLabel: 'Requested at (IST)',
  changeMadeAtText: 'recorded by the system when you save',
  bookedAt: undefined
};

// Shown when the booking change saved but the request details did not: Retry resends only the details (same uuid).
// A warning from the record save (A-52) shows its step: acknowledging resends the record with
// request_time_ack or pay_time_ack (read_care_save_warning / with_care_ack); "Change time" closes, as the
// change is saved (fix the time under Change requests).
export function CareSaveRetry(props) {
  const { message, busy, onRetry, onClose, warning, requestedAt } = props;
  if (is_pay_time_warning(warning)) {
    return (
      <PayTimeWarningStep
        warning={warning}
        busy={busy}
        changeLabel="Change time"
        onAcknowledge={(ack) => onRetry(ack)}
        onChange={onClose}
      />
    );
  }
  if (warning) {
    return (
      <RequestTimeWarningStep
        warning={warning}
        requestedLocal={requestedAt ? ist_input_value(Date.parse(requestedAt)) : ''}
        busy={busy}
        onAcknowledge={(ack) => onRetry(ack)}
        onChangeTime={onClose}
      />
    );
  }
  return (
    <div role="alert" className="care-save-retry">
      <p className="myt-form-error">
        The session change was saved, but the request details were not: {message}
      </p>
      <FormActions>
        <button type="button" className="button-secondary" disabled={busy} onClick={onClose}>
          Close (add details later from the session page)
        </button>
        <button type="button" disabled={busy} onClick={() => onRetry()}>
          {busy ? 'Saving…' : 'Retry saving details'}
        </button>
      </FormActions>
    </div>
  );
}

CareSaveRetry.propTypes = {
  message: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // read_care_save_warning(e): a request-time {warning} or a pay-time {warnings} step
  warning: PropTypes.object,
  // the record's requested_at (UTC ISO), shown on the warning step
  requestedAt: PropTypes.string
};

CareSaveRetry.defaultProps = { busy: false, warning: null, requestedAt: '' };
